/**
 * DailyCache
 *
 * Tiny dependency-free key/value cache backed by IndexedDB, with a per-entry
 * expiry (defaults to one day). Intended for dashboard data that changes at
 * most daily (e.g. "How to get started", "This week's downloads") so we can
 * render instantly from cache and avoid re-hitting the API on every visit.
 *
 * Every operation is defensive: if IndexedDB is unavailable or errors, reads
 * resolve to `null` and writes resolve quietly, so callers can always fall
 * back to the network without special-casing storage failures.
 */

const DB_NAME = 'lcp-cache';
const STORE = 'kv';
const VERSION = 1;
const DAY_MS = 24 * 60 * 60 * 1000;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise(function (resolve, reject) {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = function () {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = function () { resolve(req.result); };
    req.onerror = function () { reject(req.error); };
  });
  // Don't cache a rejected promise — allow a later retry.
  dbPromise.catch(function () { dbPromise = null; });
  return dbPromise;
}

function runTx(mode, fn) {
  return openDb().then(function (db) {
    return new Promise(function (resolve, reject) {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      tx.oncomplete = function () { resolve(request ? request.result : undefined); };
      tx.onerror = function () { reject(tx.error); };
      tx.onabort = function () { reject(tx.error); };
    });
  });
}

const DailyCache = {
  /**
   * Resolve the stored value for `key`, or `null` if missing/expired/errored.
   */
  get: function (key) {
    return runTx('readonly', function (store) { return store.get(key); })
      .then(function (record) {
        if (!record) return null;
        if (record.expires && record.expires < Date.now()) return null;
        return record.value;
      })
      .catch(function () { return null; });
  },

  /**
   * Store `value` under `key` with a time-to-live (default: one day).
   */
  set: function (key, value, ttl) {
    const expires = Date.now() + (typeof ttl === 'number' ? ttl : DAY_MS);
    return runTx('readwrite', function (store) {
      return store.put({ value: value, expires: expires }, key);
    }).catch(function () { /* ignore write failures */ });
  },

  DAY_MS: DAY_MS
};

export default DailyCache;



// WEBPACK FOOTER //
// ./src/helpers/DailyCache/index.js